// noinspection DuplicatedCode

import {useContext, useEffect} from "react";
import EditQRCodeContext from "../EditQRCodeContext";
import {useForm, UseFormInput} from "@mantine/form";
import {Button, Stack, Textarea, TextInput} from "@mantine/core";
import VCardApi from "../../../../api/vcard/VCardApi";
import Notifications from "../../../../utils/notifications/Notifications";
import UpdatePayload from "../../../../api/vcard/UpdatePayload";
import {useNavigate} from "react-router-dom";

type UpdateVCardForm = {
    firstName: string
    lastName: string,
    summary?: string | null;
    phoneNumber?: string | null;
    emailAddress?: string | null;
    websiteUrl?: string | null;
    companyName?: string | null;
    position?: string | null;
}

const convertIfEmpty = (e: string | undefined | null) => {
    if (!e) {
        return undefined;
    }
    return e.length > 0 ? e : undefined;
}

const transformValues = (values: UpdateVCardForm) => {
    return {
        ...values,
        summary: convertIfEmpty(values.summary),
        phoneNumber: convertIfEmpty(values.phoneNumber),
        emailAddress: convertIfEmpty(values.emailAddress),
        companyName: convertIfEmpty(values.companyName),
        position: convertIfEmpty(values.position),
        websiteUrl: convertIfEmpty(values.websiteUrl),
    };
};

const formDef: UseFormInput<UpdateVCardForm> = {
    mode: 'uncontrolled',
    transformValues: transformValues,
    initialValues: {
        firstName: '',
        lastName: '',
        summary: '',
        phoneNumber: '',
        emailAddress: '',
        websiteUrl: '',
        companyName: '',
        position: ''
    },

    validate: {
        firstName: e => e.length >= 3 ? null : 'Przynajmniej 3 znaki',
        lastName: e => e.length >= 3 ? null : 'Przynajmniej 3 znaki'
    }
}

function VCardEditForm() {
    /**
     * EditQRCode context.
     * @author Kacper Faber
     * @since 0.2.0
     */
    const ctx = useContext(EditQRCodeContext);

    const form = useForm(formDef);

    /**
     * Navigate function
     * @author Kacper Faber
     * @since 0.2.0
     */
    const navigate = useNavigate();

    /**
     *
     * @author Kacper Faber
     * @since 0.2.0
     */
    const onSubmitSuccess = () => {
        Notifications.onUpdateSuccess();
    }

    const onSubmitFailed = () => {
        Notifications.onUpdateFailed();
    }

    /**
     * Navigate to previous page.
     * @author Kacper Faber
     * @since 0.2.0
     */
    const goBack = () => {
        navigate(-1);
    }

    const onSubmit = (values: UpdateVCardForm) => {
        const vCardId = ctx.qrCode?.vCard?.id;

        if (!ctx.qrCode || !vCardId) {
            return;
        }

        const payload: Omit<UpdatePayload, "id"> = { ...values };

        VCardApi.updateById(vCardId, payload)
            .then(onSubmitSuccess)
            .then(goBack)
            .catch(onSubmitFailed);
    };

    const reloadFormValues = () => {
        form.setValues({...ctx.qrCode?.vCard});
    }

    useEffect(reloadFormValues, [ctx.qrCode]);

    return (
        <form onSubmit={form.onSubmit(onSubmit)}>
            Edycja kodu QR o ID

            <Stack>
                <TextInput
                    required
                    placeholder="Imię"
                    key={form.key('firstName')}
                    {...form.getInputProps('firstName')}
                />

                <TextInput
                    required
                    placeholder="Nazwisko"
                    key={form.key('lastName')}
                    {...form.getInputProps('lastName')}
                />

                <Textarea
                    placeholder="Podsumowanie"
                    key={form.key('summary')}
                    {...form.getInputProps('summary')}
                />

                <TextInput
                    type="number"
                    placeholder="Numer telefonu"
                    description="Opcjonalny"
                    key={form.key('phoneNumber')}
                    {...form.getInputProps('phoneNumber')}
                />

                <TextInput
                    type="email"
                    placeholder="Adres E-mail"
                    description="Opcjonalny"
                    key={form.key('emailAddress')}
                    {...form.getInputProps('emailAddress')}
                />

                <TextInput
                    placeholder="Adres strony"
                    key={form.key('websiteUrl')}
                    {...form.getInputProps('websiteUrl')}
                />

                <TextInput
                    placeholder="Nazwa firmy"
                    key={form.key('companyName')}
                    {...form.getInputProps('companyName')}
                />

                <TextInput
                    placeholder="Stanowisko w firmie"
                    key={form.key('position')}
                    {...form.getInputProps('position')}
                />
            </Stack>

            <Button
                type="submit"
                children="ZAPISZ"
            />
        </form>
    );
}

export default VCardEditForm;